/** Homepage KPI-row maths (POS2-869): the previous-period window and WoW delta. */

import type { WasteFilters } from "@/hooks/use-waste-filters";

export type KpiDateRange = { from: string | null; to: string | null };

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The previous window for the week-over-week pill. Null when the range is
 * unbounded (no `from`) — there's nothing to compare. An open-ended current
 * period (`to` is null) measures up to `now`.
 *
 * `weekAligned` (the week-anchored presets "this week so far" / "last week")
 * shifts the window back exactly 7 days so the comparison stays weekday-aligned
 * (POS2-956): catering waste is weekday-weighted, so comparing this week's
 * weekdays against last week's weekend — where the immediately-preceding block
 * lands for a partial week — makes the WoW meaningless. A full week shifted 7
 * days equals the immediately-preceding week, so "last week" is unchanged.
 *
 * Otherwise (custom / arbitrary spans, where a 7-day shift isn't meaningful) the
 * previous window is the equal-length block immediately before: `[from - length, from)`.
 */
export function previousPeriodRange(
  dateRange: KpiDateRange,
  now: Date,
  weekAligned: boolean,
): KpiDateRange | null {
  if (!dateRange.from) return null;
  const fromMs = new Date(dateRange.from).getTime();
  const toMs = dateRange.to ? new Date(dateRange.to).getTime() : now.getTime();
  if (weekAligned) {
    return {
      from: new Date(fromMs - WEEK_MS).toISOString(),
      to: new Date(toMs - WEEK_MS).toISOString(),
    };
  }
  const lengthMs = Math.max(0, toMs - fromMs);
  return {
    from: new Date(fromMs - lengthMs).toISOString(),
    to: new Date(fromMs).toISOString(),
  };
}

/**
 * Signed change of `current` against `previous`, as a percentage (e.g. +12.5).
 * Null when there's no positive baseline to divide by.
 */
export function wowPercent(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return ((current - previous) / previous) * 100;
}

/**
 * Waste grams per cover = (kg waste × 1000) ÷ covers — the Waste-per-cover KPI (POS2-921).
 * Null when covers is missing or non-positive (KPI shows N/A; never divides by zero).
 */
export function wastePerCoverGrams(totalWeightKg: number, covers: number | null): number | null {
  if (covers == null || covers <= 0) return null;
  return (totalWeightKg * 1000) / covers;
}

/**
 * True when the filters keep only part of each day: a time-of-day window or a service. Food spend
 * and covers are whole-day figures, so Waste %, Waste per cover and Kitchen Display's target are
 * withheld then rather than divide a slice by a whole day (POS2-1492).
 */
export function isPartOfDay(
  filters: Pick<WasteFilters, "time_from" | "time_to" | "service_time">,
): boolean {
  return !!(filters.time_from || filters.time_to || filters.service_time);
}
